import React, { useState, useEffect } from 'react';
import { Box, ThemeProvider, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../style/theme';
import { PasswordForm } from './PasswordForm';
import forgetpassback from "../../Images/forgotpassback.png"
import AnimatedOverlayShowcase from '../../components/AnimatedOverlayShowcase';

export function PasswordPage() {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const navigate = useNavigate();

  useEffect(() => {
    const handleMouseMove = (e) => {
      const leftPanel = document.querySelector('.password-left-panel');
      if (leftPanel) {
        const rect = leftPanel.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
      }
    };

    const leftPanel = document.querySelector('.password-left-panel');
    if (leftPanel) {
      leftPanel.addEventListener('mousemove', handleMouseMove);
      return () => leftPanel.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Left Panel - Branding */}
        <Box
          className="password-left-panel"
          sx={{
            flex: 1,
            background: '#3044A9',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'none',
          }}
        >
          {/* Enhanced Grid Pattern Background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              opacity: 0.25,
            }}
          />

          {/* Cursor Following Grid Highlight */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              maskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Fine Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
              opacity: 0.2,
            }}
          />

          {/* Cursor Following Fine Grid */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
              maskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Cursor Glow Effect */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 3,
              background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.06) 0%, transparent 70%)`,
              transition: 'background 0.2s ease-out',
              pointerEvents: 'none',
            }}
          />

          {/* Animated Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.06) 0%, transparent 50%),
                radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
              `,
              animation: 'gridPulse 8s ease-in-out infinite',
              '@keyframes gridPulse': {
                '0%, 100%': {
                  opacity: 0.3,
                },
                '50%': {
                  opacity: 0.6,
                },
              },
            }}
          />

          {/* Depth and Glow Effects */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              background: `
                radial-gradient(ellipse at center, rgba(255, 255, 255, 0.02) 0%, transparent 70%),
                linear-gradient(45deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
                linear-gradient(-45deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
              `,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '10%',
                left: '10%',
                right: '10%',
                bottom: '10%',
                background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 6s ease-in-out infinite',
              },
              '@keyframes float': {
                '0%, 100%': {
                  transform: 'translateY(0px) scale(1)',
                  opacity: 0.3,
                },
                '50%': {
                  transform: 'translateY(-10px) scale(1.05)',
                  opacity: 0.5,
                },
              },
            }}

          />

          {/* SkyShift Logo at Top Left */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 4,
              p: 4,
            }}
          >
            <Typography
              onClick={() => navigate('/')}
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { md: '2rem', lg: '3rem' },
                background: 'linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                width: 'fit-content',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              SkyShift
            </Typography>
          </Box>

          {/* Centered Content Container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              zIndex: 10,
              px: 4,
              height: '100%',
              gap: 4,
            }}
          >
            {/* Text Content */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { md: '2.5rem', lg: '3rem' },
                  background: 'linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Forgot Password
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { md: '1.1rem', lg: '1.25rem' },
                  maxWidth: '600px',
                }}
              >
                Secure password recovery with advanced authentication protocols
              </Typography>
            </Box>

            {/* AnimatedOverlayShowcase Component */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AnimatedOverlayShowcase
                backgroundSrc={forgetpassback}
              />
            </Box>
          </Box>
        </Box>

        {/* Right Panel - Password Form */}
        <Box
          sx={{
            flex: { xs: 1, md: 0.6 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            p: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: '400px',
              p: { xs: 3, sm: 4 },
              backgroundColor: 'transparent',
            }}
          >
            <PasswordForm />
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default PasswordPage;
